/**
 * Extracts a user-friendly error message from an RTK Query error.
 * - Auth lapse (401) → friendly copy.
 * - Other FetchBaseQueryError with server message → pass through error.data.message.
 * - No message available → return fallback.
 */

export const TOKEN_EXPIRED_MESSAGE =
  "Your session has expired. Please refresh the page to sign in again.";

export const EXPIRED_TOKEN_NOTIFICATION_ID = "expired-token";

const hasDataMessage = (
  error: unknown,
): error is { data: { message: string } } =>
  typeof error === "object" &&
  error !== null &&
  "data" in error &&
  typeof (error as { data: unknown }).data === "object" &&
  (error as { data: unknown }).data !== null &&
  "message" in (error as { data: { message: unknown } }).data &&
  typeof (error as { data: { message: string } }).data.message === "string";

/**
 * Returns true if the error represents an authentication lapse:
 * expired token, missing/invalid token, or client-side sessionExpired short-circuit.
 * All 401s in Gaia originate at API Gateway's Cognito authorizer — there is no
 * 401 in the Coral model — so status alone is sufficient.
 */
export const isAuthLapseError = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) return false;
  return (error as { status?: unknown }).status === 401;
};

export const getQueryErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (isAuthLapseError(error)) return TOKEN_EXPIRED_MESSAGE;
  if (!hasDataMessage(error)) return fallback;
  const serverMessage = error.data.message;
  return serverMessage.length > 0 ? serverMessage : fallback;
};
